import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Search, Camera, ScanBarcode, ShoppingCart, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { api, errMsg } from "../lib/api";
import { rp, beep } from "../lib/format";
import { hardware } from "../lib/hardware";
import { useAuth } from "../context/AuthContext";
import { promoDiscount } from "../lib/loyalty";
import { addToQueue, buildOfflineTx, cacheSet, isNetErr, newClientId } from "../lib/offline";
import { useHardware } from "../context/HardwareContext";
import { useBarcodeScanner } from "../hooks/useBarcodeScanner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "../components/ui/sheet";
import { ProductThumb } from "../components/common";
import { CameraScanner } from "../components/CameraScanner";
import { CartPanel } from "../components/kasir/CartPanel";
import { PaymentDialog } from "../components/kasir/PaymentDialog";
import { ReceiptDialog } from "../components/kasir/ReceiptDialog";
import { HeldDialog } from "../components/kasir/HeldDialog";

const ProductCard = ({ p, color, onAdd }) => (
  <button onClick={() => onAdd(p)} disabled={p.stock <= 0} data-testid={`product-card-${p.id}`}
    className="group text-left rounded-xl border bg-card overflow-hidden transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/10 disabled:opacity-50 disabled:pointer-events-none">
    <div className="relative aspect-[4/3] overflow-hidden">
      <ProductThumb product={p} color={color} className="h-full w-full text-2xl transition-transform duration-300 group-hover:scale-105" />
      <span className={`absolute top-2 right-2 text-[11px] font-mono font-semibold px-2 py-0.5 rounded-full backdrop-blur ${
        p.stock <= 0 ? "bg-destructive text-white" : p.stock <= p.min_stock ? "bg-amber-500 text-white" : "bg-black/50 text-white"}`}>
        {p.stock <= 0 ? "Habis" : `Stok ${p.stock}`}
      </span>
    </div>
    <div className="p-3">
      <div className="text-sm font-semibold line-clamp-2 leading-snug min-h-[2.5rem]">{p.name}</div>
      <div className="font-mono font-bold text-primary mt-1">{rp(p.price)}</div>
    </div>
  </button>
);

const loadHeld = () => JSON.parse(localStorage.getItem("pos_held") || "[]");

export default function Kasir() {
  const { store, user, can } = useAuth();
  const { cfg, printReceipt } = useHardware();
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [cart, setCart] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [discType, setDiscType] = useState("rp");
  const [payOpen, setPayOpen] = useState(false);
  const [paying, setPaying] = useState(false);
  const [doneTx, setDoneTx] = useState(null);
  const [camOpen, setCamOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [held, setHeld] = useState(loadHeld);
  const [heldOpen, setHeldOpen] = useState(false);
  const searchRef = useRef(null);
  const [promos, setPromos] = useState([]);
  const [hasShift, setHasShift] = useState(true);
  useEffect(() => { api.get("/shifts/current").then((r) => setHasShift(!!r.data)); }, []);

  const loadProducts = useCallback(() => api.get("/products").then((r) => setProducts(r.data)), []);
  useEffect(() => {
    loadProducts();
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/promotions/active").then((r) => setPromos(r.data));
    api.get("/settings").catch(() => {});
    if (can("pos") || can("customers")) api.get("/customers").catch(() => {});
  }, [loadProducts]);

  const catColor = useMemo(() => Object.fromEntries(cats.map((c) => [c.id, c.color])), [cats]);
  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    return products.filter((p) => (cat === "all" || p.category_id === cat) && (!s || p.name.toLowerCase().includes(s) || p.barcode.includes(s) || p.sku.toLowerCase().includes(s)));
  }, [products, cat, q]);

  const addToCart = useCallback((p, qty = 1) => {
    setCart((c) => {
      const ex = c.find((i) => i.product.id === p.id);
      const cur = ex ? ex.qty : 0;
      if (cur + qty > p.stock) {
        toast.error(`Stok ${p.name} tidak mencukupi (sisa ${p.stock})`);
        return c;
      }
      if (ex) return c.map((i) => (i.product.id === p.id ? { ...i, qty: i.qty + qty } : i));
      return [...c, { product: p, qty }];
    });
  }, []);

  const handleScan = useCallback(
    async (code) => {
      const local = products.find((p) => p.barcode === code || p.sku === code);
      try {
        const p = local || (await api.get(`/products/barcode/${encodeURIComponent(code)}`)).data;
        if (cfg.scanBeep) beep();
        addToCart(p);
        toast.success(`${p.name} ditambahkan`, { duration: 1200 });
      } catch (e) {
        toast.error(errMsg(e));
      }
    },
    [products, addToCart, cfg.scanBeep]
  );

  useBarcodeScanner(handleScan, { enabled: !payOpen && !doneTx });
  useEffect(() => hardware.onScan(handleScan), [handleScan]);

  const setQty = (id, qty) =>
    setCart((c) =>
      qty <= 0
        ? c.filter((i) => i.product.id !== id)
        : c.map((i) => {
            if (i.product.id !== id) return i;
            if (qty > i.product.stock) {
              toast.error(`Stok maksimal ${i.product.stock}`);
              return { ...i, qty: i.product.stock };
            }
            return { ...i, qty };
          })
    );
  const remove = (id) => setCart((c) => c.filter((i) => i.product.id !== id));
  const clear = () => { setCart([]); setDiscount(0); };

  const taxPercent = Number(store?.tax_percent || 0);
  const promoMap = useMemo(() => Object.fromEntries(cart.map((i) => [i.product.id, promoDiscount(promos, i.product, i.qty)])), [cart, promos]);
  const totals = useMemo(() => {
    const subtotal = cart.reduce((a, b) => a + b.product.price * b.qty - (promoMap[b.product.id]?.discount || 0), 0);
    const disc = Math.min(subtotal, discType === "pct" ? Math.round((subtotal * Math.min(discount, 100)) / 100) : discount);
    const tax = Math.round(((subtotal - disc) * taxPercent) / 100);
    return { subtotal, discount: disc, tax, total: subtotal - disc + tax };
  }, [cart, discount, discType, taxPercent, promoMap]);

  const openPay = useCallback(() => cart.length && setPayOpen(true), [cart.length]);

  useEffect(() => {
    const h = (e) => {
      if (e.key === "F2") { e.preventDefault(); searchRef.current?.focus(); }
      if (e.key === "F9") { e.preventDefault(); openPay(); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [openPay]);

  const finishTx = (data) => {
    setPayOpen(false);
    setCartOpen(false);
    setDoneTx(data);
    clear();
    const drawer = cfg.autoDrawer && data.payment_method === "tunai";
    if (cfg.autoPrint) printReceipt(data, { withDrawer: drawer });
    else if (drawer) hardware.openDrawer(cfg.drawerVia, Number(cfg.drawerPin)).catch(() => {});
  };

  const payOffline = (body, payload) => {
    if (payload.redeem_points) return toast.error("Tukar poin membutuhkan koneksi internet");
    const tx = buildOfflineTx({ cart, promoMap, discount: totals.discount, taxPercent, payload, user, clientId: body.client_id });
    addToQueue({ body: { ...body, offline_created_at: tx.created_at, offline_invoice: tx.invoice }, tx });
    const qty = Object.fromEntries(cart.map((i) => [i.product.id, i.qty]));
    const next = products.map((p) => (qty[p.id] ? { ...p, stock: p.stock - qty[p.id] } : p));
    setProducts(next);
    cacheSet("/products", next);
    toast.warning("Offline: transaksi disimpan di perangkat", { description: "Akan disinkronkan otomatis saat online." });
    finishTx(tx);
  };

  const pay = async (payload) => {
    setPaying(true);
    const body = { items: cart.map((i) => ({ product_id: i.product.id, qty: i.qty })), discount: totals.discount, client_id: newClientId(), ...payload };
    try {
      if (!navigator.onLine) return payOffline(body, payload);
      const { data } = await api.post("/transactions", body);
      finishTx(data);
      loadProducts();
    } catch (e) {
      if (isNetErr(e)) payOffline(body, payload);
      else toast.error(errMsg(e));
    } finally {
      setPaying(false);
    }
  };

  const hold = () => {
    const next = [{ id: Date.now().toString(36), items: cart, created: new Date().toISOString() }, ...held];
    setHeld(next);
    localStorage.setItem("pos_held", JSON.stringify(next));
    clear();
    toast.info("Transaksi ditahan");
  };
  const delHeld = (id) => {
    const next = held.filter((h) => h.id !== id);
    setHeld(next);
    localStorage.setItem("pos_held", JSON.stringify(next));
  };
  const resume = (h) => {
    const fresh = h.items.map((i) => ({ ...i, product: products.find((p) => p.id === i.product.id) || i.product }));
    setCart(fresh);
    delHeld(h.id);
    setHeldOpen(false);
  };

  const onSearchEnter = (e) => {
    if (e.key !== "Enter" || !q.trim()) return;
    const exact = products.find((p) => p.barcode === q.trim() || p.sku === q.trim());
    if (exact) { addToCart(exact); if (cfg.scanBeep) beep(); setQ(""); }
    else if (filtered.length === 1) { addToCart(filtered[0]); setQ(""); }
    else if (/^\d{6,}$/.test(q.trim())) { handleScan(q.trim()); setQ(""); }
  };

  const cartProps = { cart, promoMap, setQty, remove, clear, discount, setDiscount, discType, setDiscType, totals, taxPercent, onPay: openPay, onHold: hold, heldCount: held.length, onShowHeld: () => setHeldOpen(true) };

  return (
    <div className="grid grid-cols-12 gap-4 p-4 lg:h-[calc(100vh-4rem)] lg:overflow-hidden">
      <section className="col-span-12 lg:col-span-7 xl:col-span-8 flex flex-col min-h-0 pb-24 lg:pb-0">
        {!hasShift && (
          <div className="mb-3 flex items-center gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-2.5 text-sm" data-testid="no-shift-banner">
            <Clock className="h-4 w-4 text-warning shrink-0" />
            <span className="flex-1">Shift belum dibuka. Buka shift agar kas awal & tutup kasir tercatat.</span>
            <Button asChild size="sm" variant="outline" data-testid="no-shift-open-btn"><Link to="/shift">Buka Shift</Link></Button>
          </div>
        )}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input ref={searchRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onSearchEnter} data-scan-target="1"
              placeholder="Cari / scan barcode (F2)" className="pl-9 pr-11 h-11" data-testid="pos-search-input" />
            <ScanBarcode className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          </div>
          <Button variant="outline" className="h-11" onClick={() => setCamOpen(true)} data-testid="camera-scan-btn" title="Scan dengan kamera">
            <Camera className="h-4 w-4" /><span className="hidden sm:inline ml-2">Kamera</span>
          </Button>
        </div>
        <div className="flex gap-2 overflow-x-auto no-scrollbar py-3">
          {[{ id: "all", name: "Semua" }, ...cats].map((c) => (
            <button key={c.id} onClick={() => setCat(c.id)} data-testid={`category-pill-${c.id}`}
              className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${cat === c.id ? "bg-primary text-primary-foreground border-primary" : "hover:bg-accent"}`}>
              {c.name}
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto thin-scroll pr-1" data-testid="product-grid">
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
            {filtered.map((p) => <ProductCard key={p.id} p={p} color={catColor[p.category_id]} onAdd={addToCart} />)}
          </div>
          {filtered.length === 0 && <p className="text-center text-muted-foreground py-16 text-sm">Produk tidak ditemukan.</p>}
        </div>
      </section>
      <aside className="hidden lg:flex col-span-5 xl:col-span-4 flex-col bg-card border rounded-2xl shadow-lg p-4 min-h-0">
        <CartPanel {...cartProps} />
      </aside>

      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 p-3 bg-background/90 backdrop-blur-xl border-t">
        <Button className="w-full h-14 justify-between text-base" onClick={() => setCartOpen(true)} data-testid="mobile-cart-btn">
          <span className="flex items-center gap-2"><ShoppingCart className="h-5 w-5" />{cart.reduce((a, b) => a + b.qty, 0)} item</span>
          <span className="font-mono font-bold">{rp(totals.total)}</span>
        </Button>
      </div>
      <Sheet open={cartOpen} onOpenChange={setCartOpen}>
        <SheetContent side="bottom" className="h-[88vh] flex flex-col p-4">
          <SheetTitle className="sr-only">Keranjang</SheetTitle>
          <CartPanel {...cartProps} />
        </SheetContent>
      </Sheet>

      <PaymentDialog open={payOpen} onOpenChange={setPayOpen} base={totals.subtotal - totals.discount} taxPercent={taxPercent} store={store} onPay={pay} loading={paying} />
      <ReceiptDialog tx={doneTx} onClose={() => { setDoneTx(null); searchRef.current?.focus(); }} />
      <CameraScanner open={camOpen} onOpenChange={setCamOpen} onResult={handleScan} />
      <HeldDialog open={heldOpen} onOpenChange={setHeldOpen} held={held} onResume={resume} onDelete={delHeld} />
    </div>
  );
}
